<template>
  <article v-if="!error" class="flex flex-col space-y-4 lg:space-y-6">
    <!-- Page header -->
    <UserProfileHeader :username="username" :user="user" :loading="pending">
      <template #tabs>
        <Tabs v-model="current" :tabs="paths" no-emit />
      </template>
    </UserProfileHeader>
    <PageContainer>
      <NamiLoading v-if="pending" />
      <client-only v-else-if="user">
        <NuxtPage :user="user" keepalive />
      </client-only>
    </PageContainer>
  </article>
  <ErrorPage v-else :error />
</template>

<script setup lang="ts">
import { isUserProfile, User } from "~/src/api";
import { ApiToNuxtError } from "~/utils/errors/404";
import { placeholders } from "~/composables/media";
import { useQuery } from "@tanstack/vue-query";
import { useOrganizationGetBySlug } from "~/utils/tanstack/organization";
import { useUsername } from "~/composables/query/user";

const route = useRoute();
const nuxtApp = useNuxtApp();

const { locale, t: translate } = useI18n();

const username = computed(() => route.params.username as string);

const { current, paths } = useSubpaths(
  computed(() => ({
    default: "/",
    rootPath: linkTo("user", username.value),
    paths: [
      // { name: translate("site.user.id.slug.tabHome"), value: "home" },
      //{ name: translate("site.user.id.slug.tabPosts"), value: "info" },
      { name: translate("site.user.id.slug.tabPosts"), value: "/" },
      { name: "Following", value: "/following" },
      { name: "Followers", value: "/followers" },
      { name: translate("site.user.id.slug.tabOrgs"), value: "/organizations" },
      { name: translate("site.user.id.slug.tab.lists"), value: "/lists" },
      {
        name: translate("site.user.id.slug.tab.achievements"),
        value: "/achievements",
      },
    ],
  })),
);

const {
  data: user,
  error,
  isPending: pending,
  suspense,
} = useUsername(username, ["profile_frame", "user_profile", "badge"]);

onServerPrefetch(async () => {
  await suspense();

  if (!user.value) return;

  const seo = {
    name:
      user.value?.attributes.displayName ??
      user.value?.attributes.username ??
      "",
    description:
      user.value?.relationships.find(isUserProfile)?.attributes?.about ?? "",
    href: user.value
      ? `${config.public.baseUrl}${linkTo(user.value)}`
      : `${config.public.baseUrl}${route.fullPath}`,
    imgUrl: user.value ? getUserAvatar(user.value) : placeholders.avatar,
  };

  nuxtApp.runWithContext(() =>
    useServerSeoMeta({
      title: seo.name,
      ogTitle: seo.name,
      twitterTitle: seo.name,
      description: seo.description,
      ogDescription: seo.description,
      twitterDescription: seo.description,
      ogUrl: seo.href,
      ogLocale: locale.value,
      ogImage: seo.imgUrl,
      ogImageSecureUrl: seo.imgUrl,
      twitterImage: seo.imgUrl,
      ogImageHeight: "1200",
      ogImageWidth: "630",
      ogImageUrl: seo.imgUrl,
      ogImageType: "image/png",
      ogImageAlt: "NamiComi",
      ogLocaleAlternate: "en_GB",
    }),
  );
});

use404Response(ApiToNuxtError(error));

const config = useRuntimeConfig();
const { getUserAvatar } = useMediaLink();

useHead({
  title: () =>
    user.value?.attributes.displayName ??
    user.value?.attributes.username ??
    "Loading...",
  link: () =>
    user.value
      ? [
          {
            rel: "canonical",
            href: `${config.public.baseUrl}/en/user/${user.value.attributes.username}`,
          },
        ]
      : [],
  meta: () => [
    {
      property: "og:title",
      content: `${user.value?.attributes.displayName ?? user.value?.attributes.username ?? "Loading..."} - ${config.siteName}`,
    },
    {
      name: "description",
      content:
        user.value?.relationships.find(isUserProfile)?.attributes?.about ?? "",
    },
  ],
});
</script>
